import React, { useState, useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { useToken } from '../../hooks/useToken';
import {
  fetchManagedKeyAccountDetails,
  updateManagedKeyAccount,
  confirmSnowflakeConnection,
  selectManagedKeyData,
  selectDialogError,
  clearDialogError,
} from '../../reducers/snowflakeAccountsSlice';
import { SQLSetupSection } from './SQLSetupSection';
import { AccountSetupProgress } from './AccountSetupProgress';
import Button from '../controls/Button';
import { Input } from '../controls/Input';
import {
  ExclamationTriangleIcon,
  CheckCircleIcon,
  UserIcon,
  CircleStackIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import type { ISnowflakeAccount } from '../../interfaces/ISnowflakeAccountManagement';
import { generateCompleteSqlBlock } from '../../utilities/snowflakeUtils';

interface EditManagedKeyFormProps {
  account: ISnowflakeAccount;
  onSuccess: () => void;
  onCancel: () => void;
  initialStep?: number;
}

/**
 * Multi-step form for editing a Snowflake account with managed key authentication
 *
 * Flow (3 main steps):
 * 1. Edit username, warehouse, and role
 * 2. Display SQL commands with copy functionality and Snowsight button
 * 3. Test connection and show success
 *
 * State management:
 * - Uses local state for: username, warehouse, role, currentStep
 * - Uses Redux for: managed key data, account update, connection testing
 * - Handles loading states during account update and connection testing
 *
 * @param account - The account to edit
 * @param onSuccess - Callback when account is successfully updated and tested
 * @param onCancel - Callback to cancel the flow
 */
export function EditManagedKeyForm({ account, onSuccess, onCancel, initialStep = 1 }: EditManagedKeyFormProps) {
  const dispatch = useAppDispatch();
  const token = useToken();

  // Redux state
  const managedKeyData = useAppSelector(selectManagedKeyData);
  const dialogError = useAppSelector(selectDialogError);

  // Local state
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isTestingConnection, setIsTestingConnection] = useState(false);
  const [connectionSuccess, setConnectionSuccess] = useState(false);
  const [sqlExecuted, setSqlExecuted] = useState(false);
  const [username, setUsername] = useState<string>(account.username);
  const [warehouse, setWarehouse] = useState<string>(account.warehouse ?? 'DATAOPS_SERVICE_WAREHOUSE');
  const [role, setRole] = useState<string>(account.role ?? 'DATAOPS_SERVICE_ROLE');

  // Ref for scrolling to top when error appears
  const containerRef = useRef<HTMLDivElement>(null);

  // Fetch managed key data on mount
  useEffect(() => {
    dispatch(clearDialogError());
    if (token) {
      dispatch(fetchManagedKeyAccountDetails({ token, accountId: account.id.toString() })).catch((err) => {
        console.error('Failed to fetch account details:', err);
      });
    }
  }, [dispatch, token, account.id]);

  // Scroll to top when error appears
  useEffect(() => {
    if (dialogError && containerRef.current) {
      containerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [dialogError]);

  // Handle "Save" button click - update account
  const handleNext = async () => {
    if (!username || !warehouse || !role) {
      return;
    }

    setIsUpdating(true);
    try {
      await dispatch(
        updateManagedKeyAccount({
          token,
          accountId: account.id.toString(),
          username,
          warehouse,
          role,
        }),
      ).unwrap();
      setCurrentStep(2);
    } catch (err) {
      console.error('Failed to update account:', err);
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle test connection
  const handleTestConnection = async () => {
    if (!managedKeyData?.accountId) {
      return;
    }

    setIsTestingConnection(true);
    try {
      await dispatch(
        confirmSnowflakeConnection({
          token,
          accountId: managedKeyData.accountId.toString(),
        }),
      ).unwrap();
      setConnectionSuccess(true);
      setCurrentStep(3);

      // Wait a moment to show success message, then call onSuccess
      setTimeout(() => {
        onSuccess();
      }, 1500);
    } catch (err) {
      console.error('Failed to test connection:', err);
    } finally {
      setIsTestingConnection(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    if (!isUpdating && !isTestingConnection) {
      onCancel();
    }
  };

  return (
    <div className="space-y-6" ref={containerRef}>
      {/* Progress Indicator */}
      <AccountSetupProgress currentStep={currentStep} mode="edit" />

      {/* Error Display */}
      {dialogError && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{dialogError}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Step 1: Edit Account Details */}
      {currentStep === 1 && (
        <div className="space-y-4">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Step 1: Edit Account Details</h3>
            <p className="mt-1 text-sm text-gray-600">Update the username, warehouse, and role for this account.</p>
          </div>

          <div className="space-y-2">
            {/* Username */}
            <Input
              id="username"
              label="Username"
              icon={UserIcon}
              value={username}
              onChange={(e) => {
                setUsername(e.target.value);
              }}
              placeholder="DATAOPS_SERVICE_USER"
            />

            {/* Warehouse */}
            <Input
              id="warehouse"
              label="Warehouse"
              icon={CircleStackIcon}
              value={warehouse}
              onChange={(e) => {
                setWarehouse(e.target.value);
              }}
              placeholder="DATAOPS_SERVICE_WAREHOUSE"
            />

            {/* Role */}
            <Input
              id="role"
              label="Role"
              icon={ShieldCheckIcon}
              value={role}
              onChange={(e) => {
                setRole(e.target.value);
              }}
              placeholder="DATAOPS_SERVICE_ROLE"
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button intent="light" size="large" onClick={handleCancel} disabled={isUpdating}>
              Cancel
            </Button>
            <Button
              intent="primary"
              size="large"
              onClick={handleNext}
              disabled={!username || !warehouse || !role}
              loading={isUpdating}
            >
              Save
            </Button>
          </div>
        </div>
      )}

      {/* Step 2: SQL Setup */}
      {currentStep === 2 && managedKeyData && (
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Step 2: Configure Access</h3>
            <p className="mt-1 text-sm text-gray-600">
              Run the following SQL commands in your Snowflake account to grant access.
            </p>
          </div>

          <SQLSetupSection
            setupSQL={generateCompleteSqlBlock(username, warehouse, role, managedKeyData.publicKey)}
            accountIdentifier={account.account}
            sqlExecuted={sqlExecuted}
            onSqlExecutedChange={setSqlExecuted}
          />

          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <Button intent="light" size="large" onClick={handleCancel} disabled={isTestingConnection}>
              Cancel
            </Button>
            <Button
              intent="primary"
              size="large"
              onClick={handleTestConnection}
              disabled={!sqlExecuted}
              loading={isTestingConnection}
              aria-label="Test Snowflake connection"
            >
              Test Connection
            </Button>
          </div>
          {!sqlExecuted && (
            <p className="text-sm text-gray-500 text-right" role="status" aria-live="polite">
              Please confirm you have run the SQL commands before testing the connection.
            </p>
          )}
        </div>
      )}

      {/* Step 3: Success */}
      {currentStep === 3 && connectionSuccess && (
        <div className="space-y-6">
          <div className="rounded-md bg-green-50 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <CheckCircleIcon className="h-5 w-5 text-green-400" aria-hidden="true" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-green-800">Connection Successful!</h3>
                <div className="mt-2 text-sm text-green-700">
                  <p>Your Snowflake account has been successfully connected and is ready to use.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
